import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import * as Classes from '../common/classes';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import Underline from '@tiptap/extension-underline';
import OrderedList from '@tiptap/extension-ordered-list';
import BulletList from '@tiptap/extension-bullet-list';
import Link from '@tiptap/extension-link';
import { StarterKit } from '@tiptap/starter-kit';
import { LinkModalForm } from './LinkModalForm';
import { Color } from '@tiptap/extension-color';
import { ColorMenu } from './ColorMenu';
import TextAlign from '@tiptap/extension-text-align';
import OfficePaste from '@intevation/tiptap-extension-office-paste';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
// import { TableSizePickerContainer } from './TableSizePickerContainer';
import {
  mcLink,
  mcListFormatBulleted,
  mcListFormatNumbered,
  mcRedo,
  mcTextFormatBold,
  mcTextFormatItalic,
  mcTextFormatStrikethrough,
  mcTextFormatUnderlined,
  mcUndo,
  mcTextAlignLeft,
  mcTextAlignRight,
  mcTextJustify,
  mcTextAlignCenter,
  mcEditHTML,
  mcTextColor,
  mcTextBackgroundColorNone,
  mcTextBackgroundColorActive,
  // mcImage,
  mcFullscreen,
} from '@mc/react-ui-icons';
import { Col, Modal, Row } from '@mc/react-core';
import { Space } from '../space/Space';
import { Button, ButtonProps } from '../button/Button';
import { Icon } from '../icon/Icon';
import classNames from 'classnames';
import { SelectBox } from '../selectbox/SelectBox';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import TextStyle from '@tiptap/extension-text-style';
import { ButtonGroup } from '../button-group/ButtonGroup';
import { BgColor } from './BgColor';
// import { ImageDialogFormData, ImageModalForm } from './ImageModalForm';
import { HtmlModalForm } from './HtmlModalForm';

/**
 * Zajistí, že daná URL adresa je absolutní.
 * Pokud URL adresa nezačíná "http://" nebo "https://", přidá "https://".
 *
 * @param url - URL adresa, která má být zkontrolována a případně upravena.
 * @returns Upravená absolutní URL adresa.
 */
const ensureAbsoluteUrl = (url: string): string => {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

const WysiwygMenuBarButton = ({
  active,
  ...buttonProps
}: Omit<ButtonProps, 'style'> & {
  active?: boolean;
}) => {
  return <Button style={active ? 'default' : 'link'} {...buttonProps} />;
};
type MenuBarProps = {
  editor: Editor;
  disabled?: boolean;
  toggleFullscreen: () => void;
  fullscreen: boolean;
  onActionFinished?: () => void;
};

type HeadingItem = {
  id: string;
  name: string;
};

const headingSelectItems: HeadingItem[] = [
  {
    id: '0',
    name: 'Normální',
  },
  {
    id: '1',
    name: 'Nadpis 1',
  },
  {
    id: '2',
    name: 'Nadpis 2',
  },
  {
    id: '3',
    name: 'Nadpis 3',
  },
  {
    id: '4',
    name: 'Nadpis 4',
  },
];

const MenuBar = ({
  editor,
  disabled,
  fullscreen,
  toggleFullscreen,
}: MenuBarProps) => {
  if (!editor) {
    return null;
  }

  // const isInTable = editor?.isActive('table');

  // const isImageSelected = editor.isActive('imageComponent');

  // const handleImageModal = () => {
  //   // @ts-ignore
  //   const { node } = editor.state.selection;
  //
  //   const initialValues: Partial<ImageDialogFormData> = isImageSelected
  //     ? {
  //         width: node.attrs.width,
  //         height: node.attrs.height,
  //         title: node.attrs.title,
  //         keepRatio: node.attrs['data-keep-ratio'],
  //       }
  //     : {
  //         keepRatio: true,
  //       };
  //
  //   Modal.show(({ onClose }) => (
  //     <ImageModalForm
  //       initialValues={initialValues}
  //       createMode={!isImageSelected}
  //       onClose={onClose}
  //       onSubmit={(data) => {
  //         if (isImageSelected) {
  //           editor
  //             .chain()
  //             .focus()
  //             .updateAttributes('imageComponent', {
  //               title: data.title,
  //               width: data.width,
  //               height: data.height,
  //               'data-keep-ratio': data.keepRatio,
  //             })
  //             .run();
  //         } else {
  //           editor
  //             .chain()
  //             .focus()
  //             .setResizableImage({
  //               // @ts-ignore
  //               src: data.imgSrc,
  //               title: data.title,
  //               width: data.width,
  //               height: data.height,
  //               'data-keep-ratio': data.keepRatio,
  //             })
  //             .run();
  //         }
  //         onClose();
  //         return Promise.resolve();
  //       }}
  //     />
  //   ));
  // };

  const handleLinkModal = () => {
    const previousUrl = editor.getAttributes('link').href;

    Modal.show(({ onClose }) => (
      <LinkModalForm
        initialValues={{
          url: previousUrl,
        }}
        canDelete={Boolean(previousUrl)}
        onClose={onClose}
        onDelete={() => {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
          onClose();
        }}
        onSubmit={(data) => {
          const absoluteUrl = ensureAbsoluteUrl(data.url);
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: absoluteUrl })
            .run();
          onClose();
          return Promise.resolve();
        }}
      />
    ));
  };

  let headingValue = '0';
  if (editor.isActive('heading', { level: 1 })) {
    headingValue = '1';
  } else if (editor.isActive('heading', { level: 2 })) {
    headingValue = '2';
  } else if (editor.isActive('heading', { level: 3 })) {
    headingValue = '3';
  } else if (editor.isActive('heading', { level: 4 })) {
    headingValue = '4';
  }

  const handleHeadingChange = (value?: string) => {
    const level = typeof value !== 'undefined' ? parseInt(value, 10) : 0;
    if (level > 0) {
      // @ts-ignore
      editor.chain().focus().toggleHeading({ level }).run();
    } else {
      editor.chain().focus().setParagraph().run();
    }
  };

  const handleHtmlModal = () => {
    const rawHtml = editor?.getHTML() ?? '';

    Modal.show(({ onClose }) => (
      <HtmlModalForm
        initialValues={{
          html: rawHtml,
        }}
        onClose={onClose}
        onSubmit={(data) => {
          editor?.commands.setContent(data?.html ?? '<p></p>', true);
          onClose();
          return Promise.resolve();
        }}
      />
    ));
  };

  const [currentColor, setColor] = useState<string | undefined>(undefined);
  const [currentBgColor, setBgColor] = useState<string | undefined>(undefined);

  const handleBgColorChange = useCallback(
    (color?: string) => {
      if (!editor) return;

      if (typeof color === 'string' && color.trim() !== '') {
        editor
          .chain()
          .focus()
          //@ts-ignore
          .setBgColor(color ?? null)
          .run();
      } else {
        //@ts-ignore
        editor.chain().focus().unsetBgColor().run();
      }

      // @ts-ignore
      setBgColor(color);
    },
    [editor]
  );

  const handleColorChange = useCallback(
    (color?: string) => {
      if (!editor) return;

      if (typeof color === 'string' && color.trim() !== '') {
        //@ts-ignore
        editor
          .chain()
          .focus()
          .setColor(color ?? null)
          .run();
      } else {
        //@ts-ignore
        editor.chain().focus().unsetColor().run();
      }

      // @ts-ignore
      setColor(color);
    },
    [editor]
  );

  return (
    <Row noGutters>
      <Col>
        <Space wrap className={'p-1'} size={'2'}>
          <ButtonToolbar>
            <WysiwygMenuBarButton
              title={'Tučné'}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={
                disabled || !editor.can().chain().focus().toggleBold().run()
              }
              active={editor.isActive('bold')}
            >
              <Icon fixedWidth icon={mcTextFormatBold} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Kurzíva'}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={
                disabled || !editor.can().chain().focus().toggleItalic().run()
              }
              active={editor.isActive('italic')}
            >
              <Icon fixedWidth icon={mcTextFormatItalic} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Podtržené'}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={
                disabled ||
                !editor.can().chain().focus().toggleUnderline().run()
              }
              active={editor.isActive('underline')}
            >
              <Icon fixedWidth icon={mcTextFormatUnderlined} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Přeškrtnuté'}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={
                disabled || !editor.can().chain().focus().toggleStrike().run()
              }
              active={editor.isActive('strike')}
            >
              <Icon fixedWidth icon={mcTextFormatStrikethrough} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Odrážky'}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              disabled={
                disabled ||
                !editor.can().chain().focus().toggleBulletList().run()
              }
              active={editor.isActive('bulletList')}
            >
              <Icon fixedWidth icon={mcListFormatBulleted} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Číslování'}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              disabled={
                disabled ||
                !editor.can().chain().focus().toggleOrderedList().run()
              }
              active={editor.isActive('orderedList')}
            >
              <Icon fixedWidth icon={mcListFormatNumbered} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Odkaz'}
              onClick={handleLinkModal}
              active={editor.isActive('link')}
              disabled={
                disabled ||
                (editor.view.state.selection.from ===
                  editor.view.state.selection.to &&
                  !editor.getAttributes('link').href)
              }
            >
              <Icon fixedWidth icon={mcLink} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Zarovnat do leva'}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={
                editor.isActive({ textAlign: 'left' }) ||
                editor.isActive({ textAlign: null })
              }
              disabled={
                disabled ||
                !editor.can().chain().focus().setTextAlign('left').run()
              }
            >
              <Icon fixedWidth icon={mcTextAlignLeft} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Zarovnat na střed'}
              onClick={() =>
                editor.chain().focus().setTextAlign('center').run()
              }
              active={editor.isActive({ textAlign: 'center' })}
              disabled={
                disabled ||
                !editor.can().chain().focus().setTextAlign('center').run()
              }
            >
              <Icon fixedWidth icon={mcTextAlignCenter} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Zarovnat do prava'}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editor.isActive({ textAlign: 'right' })}
              disabled={
                disabled ||
                !editor.can().chain().focus().setTextAlign('right').run()
              }
            >
              <Icon fixedWidth icon={mcTextAlignRight} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Zarovnat do bloku'}
              onClick={() =>
                editor.chain().focus().setTextAlign('justify').run()
              }
              active={editor.isActive({ textAlign: 'justify' })}
              disabled={
                disabled ||
                !editor.can().chain().focus().setTextAlign('justify').run()
              }
            >
              <Icon fixedWidth icon={mcTextJustify} />
            </WysiwygMenuBarButton>
          </ButtonToolbar>
          <SelectBox<HeadingItem, string>
            disabled={disabled}
            useIdAsValue
            value={headingValue}
            onChange={handleHeadingChange}
            className={classNames(Classes.WYSIWYG_HEADING_SELECT)}
            inputProps={{
              className: Classes.FORM_NO_FORM_INPUT,
            }}
            items={headingSelectItems}
            renderItemContent={(x: any) => {
              switch (x?.id ?? '') {
                case '0':
                  return <div>{x.name}</div>;
                case '1':
                  return <h1>{x.name}</h1>;
                case '2':
                  return <h2>{x.name}</h2>;
                case '3':
                  return <h3>{x.name}</h3>;
                case '4':
                  return <h4>{x.name}</h4>;
                default:
                  return <div>{x.name}</div>;
              }
            }}
          />
          <ButtonToolbar>
            <ButtonGroup linkButtons>
              <WysiwygMenuBarButton
                disabled={disabled}
                title={'Barva písma'}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .setColor(currentColor ?? '')
                    .run()
                }
              >
                <Icon icon={mcTextColor} color={currentColor} />
              </WysiwygMenuBarButton>
              <WysiwygMenuBarButton
                disabled={disabled}
                noDefaultWidth
                dropdown
                menu={
                  <ColorMenu
                    value={currentColor}
                    onChange={handleColorChange}
                  />
                }
                popupProps={{ mode: 'edit' }}
              />
            </ButtonGroup>
            <ButtonGroup linkButtons>
              <WysiwygMenuBarButton
                disabled={disabled}
                title={'Zvýraznění'}
                onClick={() =>
                  //@ts-ignore
                  editor.chain().focus().setBgColor(currentBgColor).run()
                }
              >
                <Icon
                  icon={
                    currentBgColor != null
                      ? mcTextBackgroundColorActive
                      : mcTextBackgroundColorNone
                  }
                  color={currentBgColor}
                />
              </WysiwygMenuBarButton>
              <WysiwygMenuBarButton
                disabled={disabled}
                noDefaultWidth
                dropdown
                menu={
                  <ColorMenu
                    value={currentBgColor}
                    onChange={handleBgColorChange}
                  />
                }
                popupProps={{ mode: 'edit' }}
              />
            </ButtonGroup>
            {/*<WysiwygMenuBarButton*/}
            {/*  title={'Tabulka'}*/}
            {/*  menu={*/}
            {/*    <TableSizePickerContainer*/}
            {/*      onInsert={(size) => {*/}
            {/*        console.log('Insert table with size:', size);*/}
            {/*        // Here you trigger your editor table insert command*/}
            {/*        editor*/}
            {/*          ?.chain()*/}
            {/*          .focus()*/}
            {/*          .insertTable({*/}
            {/*            rows: size.rows,*/}
            {/*            cols: size.cols,*/}
            {/*            withHeaderRow: true,*/}
            {/*          })*/}
            {/*          .run();*/}
            {/*      }}*/}
            {/*    />*/}
            {/*  }*/}
            {/*  popupProps={{ mode: 'edit' }}*/}
            {/*>*/}
            {/*  <Icon icon={mcTable} />*/}
            {/*</WysiwygMenuBarButton>*/}
            {/*<WysiwygMenuBarButton*/}
            {/*  title={'Odstranit tabulku'}*/}
            {/*  disabled={!isInTable}*/}
            {/*  onClick={() => editor?.chain().focus().deleteTable().run()}*/}
            {/*>*/}
            {/*  <Icon icon={mcClear} />*/}
            {/*</WysiwygMenuBarButton>*/}
            {/*<WysiwygMenuBarButton*/}
            {/*  title={isImageSelected ? 'Upravení obrázku' : 'Vložení obrázku'}*/}
            {/*  onClick={handleImageModal}*/}
            {/*  disabled={disabled}*/}
            {/*>*/}
            {/*  <Icon icon={mcImage} />*/}
            {/*</WysiwygMenuBarButton>*/}
            <WysiwygMenuBarButton
              title={
                fullscreen ? 'Zpět z celé obrazovky' : 'Na celou obrazovku'
              }
              onClick={toggleFullscreen}
              disabled={disabled}
            >
              <Icon icon={mcFullscreen} />
            </WysiwygMenuBarButton>
            <WysiwygMenuBarButton
              title={'Zdrojový kód'}
              onClick={handleHtmlModal} //funkce pro zobrazení popupu s html editorem
              disabled={disabled}
            >
              <Icon fixedWidth icon={mcEditHTML} />
            </WysiwygMenuBarButton>
          </ButtonToolbar>
        </Space>
      </Col>
      <Col xs={'auto'} className={'p-1'}>
        <WysiwygMenuBarButton
          title={'Zpět'}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={disabled || !editor.can().chain().focus().undo().run()}
        >
          <Icon fixedWidth icon={mcUndo} />
        </WysiwygMenuBarButton>
      </Col>
      <Col xs={'auto'} className={'p-1'}>
        <WysiwygMenuBarButton
          title={'Dopředu'}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={disabled || !editor.can().chain().focus().redo().run()}
        >
          <Icon fixedWidth icon={mcRedo} />
        </WysiwygMenuBarButton>
      </Col>
    </Row>
  );
};

export type WysiwygProps = {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
};

export const Wysiwyg: FC<WysiwygProps> = ({
  disabled,
  value,
  onChange,
  onBlur,
  className,
}) => {
  const extensions = [
    Underline,
    BulletList,
    OrderedList,
    StarterKit.configure({}),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TextStyle,
    Color.configure({}),
    BgColor,
    OfficePaste,
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
    Link.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
    }),
  ];
  const currValue = useRef<string | undefined>(value);

  const editor = useEditor({
    extensions: extensions,
    content: value,
    editable: !disabled,
  });

  useEffect(() => {
    if (value != currValue.current) {
      editor?.commands.setContent(value ?? '');
    }
  }, [value]);

  useEffect(() => {
    editor?.on('update', ({ editor }) => {
      let newValue = editor.getHTML();

      if (/^<p><\/p>$/.test(editor.getHTML().trim())) {
        newValue = '';
      }

      if (currValue.current != newValue) {
        currValue.current = newValue;
        onChange?.(newValue);
      }
    });
  }, [onChange]);

  const [fullscreen, setFullscreen] = useState(false);

  return (
    <div
      className={classNames(Classes.WYSIWYG_EDITOR, className)}
      style={{
        position: fullscreen ? 'fixed' : 'relative',
        top: fullscreen ? 0 : undefined,
        left: fullscreen ? 0 : undefined,
        right: fullscreen ? 0 : undefined,
        bottom: fullscreen ? 0 : undefined,
        zIndex: fullscreen ? 1000 : undefined,
        width: fullscreen ? '100vw' : '100%',
        height: fullscreen ? '100vh' : 'auto',
        overflow: 'auto',
      }}
    >
      <div className={Classes.WYSIWYG_MENU}>
        {editor && (
          <MenuBar
            editor={editor}
            disabled={disabled}
            fullscreen={fullscreen}
            toggleFullscreen={() => setFullscreen(!fullscreen)}
          />
        )}
      </div>
      <EditorContent
        className={Classes.WYSIWYG_CONTENT}
        editor={editor}
        onBlur={onBlur}
      />
    </div>
  );
};
